import * as React from "react";
// MUI
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

// Third Party
import { UploadDropzone } from "@bytescale/upload-widget-react";
import { User as Auth0User } from "@auth0/auth0-react";
import { useMutation } from "@apollo/client";
import { Player } from "@lottiefiles/react-lottie-player";
import parse from "html-react-parser";
import Cropper from "react-easy-crop";
import { Area } from "react-easy-crop";

// Internal
import en from "../../data/en.json";
import { CREATE_PUBLICATION } from "../../data/graphql/mutations";
import { PublicationKindEnum } from "../../data/types";
import MultipleSelectChip from "../../components/MultipleSelectChip";
import RichTextEditor from "../../components/RichTextEditor";
import Success from "../../assets/animations/success.json";
import Error from "../../assets/animations/error.json";
import getCroppedImg from "../../lib/cron-image";

interface SubmitPublicationStepType {
  step: Number;
  label: String;
}

interface SubmitPublicationProps {
  user: Auth0User;
  publicationId: number | null;
}

export default function SubmitPublication(props: SubmitPublicationProps) {
  const steps: SubmitPublicationStepType[] = [
    { step: 0, label: en.common.submitPublicationSection.enterPublicationInfo },
    { step: 1, label: en.common.submitPublicationSection.uploadPublication },
    { step: 2, label: en.common.submitPublicationSection.submitPublication },
  ];

  const [
    createPublication,
    { error: CreatePublicationError, data: CreatePublicationData },
  ] = useMutation(CREATE_PUBLICATION);

  const [activeStep, setActiveStep] = React.useState<number>(0);
  const [publicationTitle, setPublicationTitle] = React.useState<string>("");
  const [publicationHeadline, setPublicationHeadline] =
    React.useState<string>("");
  const [publicationType, setPublicationType] =
    React.useState<PublicationKindEnum>(PublicationKindEnum.literature);
  const [publicationFormat, setPublicationFormat] =
    React.useState<string>("online"); // "online" or "in-print"
  const [publicationHtmlContent, setPublicationHtmlContent] = React.useState<
    string | undefined
  >(undefined);
  const [publicationPaintingUrl, setPublicationPaintingUrl] = React.useState<
    string | undefined
  >(undefined);
  const [coverPhotoUrl, setCoverPhotoUrl] = React.useState<string | undefined>(
    undefined
  );
  const [tagNames, setTagNames] = React.useState<string[]>([]);

  // Cover photo upload states
  const [coverPhotoFile, setCoverPhotoFile] = React.useState<File | null>(null);
  const [coverPhotoPreview, setCoverPhotoPreview] = React.useState<
    string | null
  >(null);
  const [coverPhotoUploadError, setCoverPhotoUploadError] = React.useState("");
  const coverPhotoInputRef = React.useRef<HTMLInputElement>(null);
  const [coverPhotoCrop, setCoverPhotoCrop] = React.useState({ x: 0, y: 0 });
  const [coverPhotoZoom, setCoverPhotoZoom] = React.useState(1);
  const [coverPhotoCroppedAreaPixels, setCoverPhotoCroppedAreaPixels] =
    React.useState<Area | null>(null);
  const [coverPhotoCroppingModalOpen, setCoverPhotoCroppingModalOpen] =
    React.useState(false);

  // Publication submission states
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [submissionSuccess, setSubmissionSuccess] = React.useState(false);
  const [submissionError, setSubmissionError] = React.useState(false);

  const handleSubmitPublication = async () => {
    setIsSubmitting(true);
    setCoverPhotoUploadError("");

    if (!coverPhotoFile || !coverPhotoPreview || !coverPhotoCroppedAreaPixels) {
      // No cover photo, use regular mutation
      try {
        const result = await createPublication({
          variables: {
            title: publicationTitle,
            headline: publicationHeadline,
            user_auth0_id: props.user?.sub,
            tag_names: tagNames,
            kind: publicationType,
            format: publicationFormat,
            painting_url: publicationPaintingUrl || null,
            html_content: publicationHtmlContent || null,
          },
        });

        if (result.data) {
          setSubmissionSuccess(true);
        } else {
          setSubmissionError(true);
        }
      } catch (error) {
        setSubmissionError(true);
      }
      setIsSubmitting(false);
      return;
    }

    // Handle cover photo upload with FormData approach (same as MyProfile)
    try {
      const croppedBlob = await getCroppedImg(
        coverPhotoPreview,
        coverPhotoCroppedAreaPixels
      );
      const croppedFile = new File([croppedBlob], coverPhotoFile.name, {
        type: "image/jpeg",
      });

      const formData = new FormData();
      formData.append(
        "operations",
        JSON.stringify({
          query: `
            mutation createPublication(
              $title: String!,
              $kind: String!,
              $headline: String!,
              $tag_names: [String!]!,
              $user_auth0_id: String!,
              $format: String!,
              $painting_url: String,
              $html_content: String,
              $cover_photo: Upload
            ) {
              createPublication(
                title: $title,
                kind: $kind,
                headline: $headline,
                tagNames: $tag_names,
                userAuth0Id: $user_auth0_id,
                format: $format,
                paintingUrl: $painting_url,
                htmlContent: $html_content,
                coverPhoto: $cover_photo
              ) { id title userId }
            }
          `,
          variables: {
            title: publicationTitle,
            headline: publicationHeadline,
            user_auth0_id: props.user?.sub,
            tag_names: tagNames,
            kind: publicationType,
            format: publicationFormat,
            painting_url: publicationPaintingUrl || null,
            html_content: publicationHtmlContent || null,
            cover_photo: null,
          },
        })
      );

      formData.append(
        "map",
        JSON.stringify({ "0": ["variables.cover_photo"] })
      );
      formData.append("0", croppedFile);

      const token = process.env.REACT_APP_INKBOWL_API_TOKEN;
      const res = await fetch(
        `${process.env.REACT_APP_INKBOWL_API_HOST}/graphql`,
        {
          method: "POST",
          body: formData,
          headers: {
            Authorization: token ? `Bearer ${token}` : "",
          },
        }
      );

      const json = await res.json();
      if (json.errors) {
        setSubmissionError(true);
        setCoverPhotoUploadError(
          "Error creating publication. Please try again."
        );
      } else {
        setSubmissionSuccess(true);
      }
    } catch (error) {
      setSubmissionError(true);
      setCoverPhotoUploadError(
        "Error processing cover photo. Please try again."
      );
    }

    setIsSubmitting(false);
  };

  const handleCoverPhotoFileChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    const allowedTypes = ["image/png", "image/jpeg", "image/jpg"];
    const maxSize = 2 * 1024 * 1024; // 2MB

    if (!allowedTypes.includes(selectedFile.type)) {
      setCoverPhotoUploadError("Only PNG, JPG, or JPEG files are allowed.");
      return;
    }
    if (selectedFile.size > maxSize) {
      setCoverPhotoUploadError("File must be smaller than 2MB.");
      return;
    }

    setCoverPhotoUploadError("");
    setCoverPhotoFile(selectedFile);
    const reader = new FileReader();
    reader.onload = () => {
      setCoverPhotoPreview(reader.result as string);
      setCoverPhotoCroppingModalOpen(true);
    };
    reader.readAsDataURL(selectedFile);
  };

  const handleCoverPhotoUpload = async () => {
    if (!coverPhotoFile || !coverPhotoPreview || !coverPhotoCroppedAreaPixels)
      return;

    try {
      const croppedBlob = await getCroppedImg(
        coverPhotoPreview,
        coverPhotoCroppedAreaPixels
      );

      // Convert to data URL for preview
      const reader = new FileReader();
      reader.onload = () => {
        setCoverPhotoUrl(reader.result as string);
        setCoverPhotoCroppingModalOpen(false);
      };
      reader.readAsDataURL(croppedBlob);
    } catch (error) {
      setCoverPhotoUploadError("Error processing image. Please try again.");
    }
  };

  function SubmitPublicationSteppers() {
    return (
      <Box sx={{ m: 1 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((step: SubmitPublicationStepType, index: number) => (
            <Step key={index}>
              <StepLabel>{step.label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>
    );
  }

  function EnterPublicationInfoStep() {
    const publicationKinds: PublicationKindEnum[] = [
      PublicationKindEnum.literature,
      PublicationKindEnum.multimedia,
      PublicationKindEnum.visualArt,
    ];

    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
        }}
      >
        <Typography variant="h4" sx={{ p: 1 }}>
          {en.common.submitPublicationSection.enterPublicationInfo}
        </Typography>

        <FormControl sx={{ width: "50vw" }}>
          <Typography variant="body1" sx={{ pt: 1 }}>
            {en.common.submitPublicationSection.publicationTitleQuestion}
          </Typography>
          <TextField
            required
            id="filled-required"
            label="Required"
            defaultValue={publicationTitle}
            onChange={(e) => {
              setPublicationTitle(e.target.value);
            }}
            variant="outlined"
          />
          <Typography variant="body1" sx={{ pt: 1 }}>
            {en.common.submitPublicationSection.publicationHeadlineQuestion}
          </Typography>

          <TextField
            required
            id="filled-required"
            label="Required"
            defaultValue={publicationHeadline}
            onChange={(e) => {
              setPublicationHeadline(e.target.value);
            }}
            variant="outlined"
          />

          <Typography variant="body1" sx={{ pt: 1 }}>
            {en.common.submitPublicationSection.addTagsToPublication}
          </Typography>
          <MultipleSelectChip tagNames={tagNames} setTagNames={setTagNames} />

          <Typography variant="body1" sx={{ pt: 1 }}>
            {en.common.submitPublicationSection.publicationTypeQuestion}
          </Typography>
          <RadioGroup
            aria-labelledby="demo-radio-buttons-group-label"
            defaultValue={publicationKinds[0]}
            name="radio-buttons-group"
          >
            {publicationKinds.map(
              (publicationKind: PublicationKindEnum, index: number) => (
                <FormControlLabel
                  key={index}
                  value={publicationKind}
                  control={<Radio />}
                  label={publicationKind}
                  onChange={() => setPublicationType(publicationKind)}
                />
              )
            )}
          </RadioGroup>

          <Typography variant="body1" sx={{ pt: 1 }}>
            Publication Format
          </Typography>
          <RadioGroup
            aria-labelledby="publication-format-group-label"
            defaultValue="online"
            name="publication-format-group"
            value={publicationFormat}
            onChange={(e) => setPublicationFormat(e.target.value)}
          >
            <FormControlLabel
              value="online"
              control={<Radio />}
              label="Digital Publication (Online)"
            />
            <FormControlLabel
              value="in-print"
              control={<Radio />}
              label="InkBowl Magazine (In-Print)"
            />
            <FormControlLabel
              value="both"
              control={<Radio />}
              label="Both Digital and In-Print"
            />
          </RadioGroup>

          <Typography variant="body1" sx={{ pt: 2 }}>
            Cover Photo (Optional)
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary", mb: 1 }}>
            Upload a .jpg or .png file (max 2MB)
          </Typography>
          <Box sx={{ mt: 1, mb: 2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Button
                variant="outlined"
                onClick={() => coverPhotoInputRef.current?.click()}
              >
                Upload Cover Photo
              </Button>
              <input
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                ref={coverPhotoInputRef}
                onChange={handleCoverPhotoFileChange}
              />
              {coverPhotoUrl && (
                <Button
                  variant="text"
                  color="error"
                  onClick={() => {
                    setCoverPhotoUrl(undefined);
                    setCoverPhotoFile(null);
                    setCoverPhotoPreview(null);
                  }}
                >
                  Remove
                </Button>
              )}
            </Box>
            {coverPhotoUploadError && (
              <Typography
                variant="caption"
                sx={{ color: "error.main", mt: 1, display: "block" }}
              >
                {coverPhotoUploadError}
              </Typography>
            )}
            {coverPhotoUrl && (
              <Box sx={{ mt: 2, textAlign: "center" }}>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Cover Photo Preview:
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <img
                    src={coverPhotoUrl}
                    alt="Cover photo preview"
                    style={{
                      maxWidth: "200px",
                      maxHeight: "200px",
                      objectFit: "cover",
                      borderRadius: "8px",
                      border: "1px solid #e0e0e0",
                    }}
                  />
                </Box>
              </Box>
            )}
          </Box>
        </FormControl>
      </Box>
    );
  }

  function UploadPublicationStep() {
    const handleEditorChange = (content: string) => {
      setPublicationHtmlContent(content);
    };

    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
        }}
      >
        <Typography variant="h4" sx={{ p: 1 }}>
          {en.common.submitPublicationSection.uploadPublication}
        </Typography>
        {[
          PublicationKindEnum.literature,
          PublicationKindEnum.multimedia,
        ].includes(publicationType) && (
          <Box sx={{ mt: 2 }}>
            <RichTextEditor
              value={publicationHtmlContent || ""}
              onChange={handleEditorChange}
              placeholder="Start writing your publication content..."
              height={500}
              width={750}
            />
          </Box>
        )}

        {publicationType === PublicationKindEnum.visualArt && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
            }}
          >
            <UploadDropzone
              options={{
                apiKey: process.env.REACT_APP_UPLOADER_API_KEY!,
                maxFileCount: 1,
                showFinishButton: false,
              }}
              onUpdate={({ uploadedFiles }) => {
                const fileUrls: string[] = uploadedFiles.map((x) => x.fileUrl);
                setPublicationPaintingUrl(fileUrls[0]);
              }}
            />
          </Box>
        )}
      </Box>
    );
  }

  function SubmitPublicationStep() {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          mt: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            mt: 3,
          }}
        >
          <Typography variant="h4" sx={{ p: 1 }}>
            {en.common.submitPublicationSection.submitPublication}
          </Typography>
        </Box>

        <Box sx={{ ml: 10 }}>
          <Typography
            variant="subtitle2"
            sx={{ pt: 1, textTransform: "capitalize" }}
          >
            {en.common.submitPublicationSection.publicationTitleQuestion}
          </Typography>
          <Typography variant="body1" sx={{ pt: 1 }}>
            {publicationTitle}
          </Typography>

          <Typography
            variant="subtitle2"
            sx={{ pt: 1, textTransform: "capitalize" }}
          >
            {en.common.submitPublicationSection.publicationHeadlineQuestion}
          </Typography>
          <Typography variant="body1" sx={{ pt: 1 }}>
            {publicationHeadline}
          </Typography>

          <Typography
            variant="subtitle2"
            sx={{ pt: 1, textTransform: "capitalize" }}
          >
            Publication Type
          </Typography>
          <Typography variant="body1" sx={{ pt: 1 }}>
            {publicationType}
          </Typography>

          <Typography
            variant="subtitle2"
            sx={{ pt: 1, textTransform: "capitalize" }}
          >
            Publication Format
          </Typography>
          <Typography variant="body1" sx={{ pt: 1 }}>
            {publicationFormat === "online"
              ? "Digital Publication (Online)"
              : publicationFormat === "in-print"
              ? "InkBowl Magazine (In-Print)"
              : "Both Digital and In-Print"}
          </Typography>

          <Typography
            variant="subtitle2"
            sx={{ pt: 1, textTransform: "capitalize" }}
          >
            {en.common.submitPublicationSection.publicationTags}
          </Typography>
          {tagNames.map((tagName: string, index: number) => (
            <Chip
              key={index}
              label={tagName}
              variant="outlined"
              sx={{ mr: 1, mt: 1 }}
            />
          ))}

          {coverPhotoUrl && (
            <>
              <Typography
                variant="subtitle2"
                sx={{ pt: 1, textTransform: "capitalize" }}
              >
                Cover Photo
              </Typography>
              <Box sx={{ mt: 1, mb: 2 }}>
                <img
                  src={coverPhotoUrl}
                  alt="Cover photo"
                  style={{
                    maxWidth: "300px",
                    maxHeight: "200px",
                    objectFit: "cover",
                    borderRadius: "8px",
                    border: "1px solid #e0e0e0",
                  }}
                />
              </Box>
            </>
          )}

          <Typography
            variant="subtitle2"
            sx={{ pt: 1, textTransform: "capitalize" }}
          >
            {en.common.submitPublicationSection.publicationContent}
          </Typography>
          {publicationHtmlContent && parse(publicationHtmlContent)}
          {publicationPaintingUrl && (
            <img width={350} src={publicationPaintingUrl} alt="painting" />
          )}
          <FormGroup>
            <FormControlLabel
              required
              control={<Checkbox />}
              label={
                en.common.submitPublicationSection.acknowledgeTermsAndConditions
              }
            />
            <FormControlLabel
              required
              control={<Checkbox />}
              label={en.common.submitPublicationSection.allowEditing}
            />
          </FormGroup>
        </Box>
      </Box>
    );
  }

  function SubmitPublicationNavigation() {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          pt: 2,
        }}
      >
        {activeStep > 0 && (
          <Button
            onClick={() => setActiveStep(activeStep - 1)}
            disabled={isSubmitting}
          >
            {en.common.previous}
          </Button>
        )}
        {activeStep < steps.length - 1 && (
          <Button
            onClick={() => setActiveStep(activeStep + 1)}
            disabled={isSubmitting}
          >
            {en.common.next}
          </Button>
        )}
        {activeStep === steps.length - 1 && (
          <Button onClick={handleSubmitPublication} disabled={isSubmitting}>
            {isSubmitting ? "Submitting..." : en.common.complete}
          </Button>
        )}
      </Box>
    );
  }

  if (submissionSuccess || CreatePublicationData !== undefined) {
    return (
      <Player
        autoplay={true}
        loop={true}
        src={Success}
        style={{ height: "300px", width: "300px" }}
      />
    );
  }

  if (submissionError || CreatePublicationError !== undefined) {
    return (
      <Player
        autoplay={true}
        loop={true}
        src={Error}
        style={{ height: "300px", width: "300px" }}
      />
    );
  }

  return (
    <React.Fragment>
      {SubmitPublicationSteppers()}

      {activeStep === 0 && EnterPublicationInfoStep()}
      {activeStep === 1 && UploadPublicationStep()}
      {activeStep === 2 && SubmitPublicationStep()}

      {SubmitPublicationNavigation()}

      <Dialog
        open={coverPhotoCroppingModalOpen}
        onClose={() => setCoverPhotoCroppingModalOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Crop Cover Photo</DialogTitle>
        <DialogContent>
          {coverPhotoPreview && (
            <Box
              sx={{
                position: "relative",
                width: "100%",
                height: 300,
              }}
            >
              <Cropper
                image={coverPhotoPreview}
                crop={coverPhotoCrop}
                zoom={coverPhotoZoom}
                aspect={16 / 9} // Use 16:9 aspect ratio for cover photos
                onCropChange={setCoverPhotoCrop}
                onZoomChange={setCoverPhotoZoom}
                onCropComplete={(_, croppedArea) =>
                  setCoverPhotoCroppedAreaPixels(croppedArea)
                }
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCoverPhotoCroppingModalOpen(false)}>
            {en.common.cancel}
          </Button>
          <Button variant="contained" onClick={handleCoverPhotoUpload}>
            Apply Crop
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
}
